/**
 * calculateOffsetMapFromManifest.ts
 *
 * Validates backward compatibility by reconstructing offset_map.json
 * from manifest.json offsets and comparing against the original.
 *
 * This file orchestrates the validation pipeline:
 * 1. Load manifest and offset_map
 * 2. Calculate positions using BFS
 * 3. Reconstruct offset_map from calculated positions
 * 4. Compare calculated vs original offsets
 * 5. Generate comprehensive validation report
 */

import { calculateFrameWorldPositionsFromManifest } from './worldSpace'

/**
 * Manifest structure from fragmented data
 */
export interface Manifest {
  version: string
  camera_models: Record<string, any>
  default_camera_model: string
  frames_index: string[]
  frames: ManifestFrame[]
}

export interface ManifestFrame {
  id: number
  frame_file: string
  offsets: Record<string, { dx: number; dy: number }>
}

/**
 * World position type (matches the one in calculateWorldPositionsBFS.ts)
 */
export interface WorldPosition {
  x: number
  y: number
}

/**
 * Original offset_map.json structure (for reference/comparison)
 */
export interface OffsetMap {
  metadata: {
    frameCount: number
    sceneCenter: [number, number, number]
    generated: string
    version: string
  }
  all_pairs: Record<
    string, // Key format: "frameA_frameB"
    {
      dx: number
      dy: number
      dz: number
      distance_3d: number
      confidence: number
      pointCount: number
      yaw_diff: number
      pitch_diff: number
      roll_diff: number
      angle_diff: number
      similar_orientation: boolean
      motion_type: string
    }
  >
}

/**
 * Offset values extracted from offset_map (for comparison)
 */
export interface OffsetPair {
  dx: number
  dy: number
  dz?: number
  distance_3d?: number
}

/**
 * Reconstructed offset_map structure (calculated from manifest)
 * Contains only dx/dy (what we're validating)
 */
export interface ReconstructedOffsetMap {
  all_pairs: Record<
    string, // Key format: "frameA_frameB"
    {
      dx: number
      dy: number
    }
  >
}

/**
 * Comparison result for a single offset pair
 */
export interface OffsetComparison {
  key: string // "frameA_frameB"
  original: { dx: number; dy: number }
  calculated: { dx: number; dy: number }
  match: boolean
  error: { dx: number; dy: number }
  errorMagnitude: number
}

/**
 * Comparison result for a single frame
 */
export interface FrameComparison {
  frameId: number
  offsetCount: number
  matchCount: number
  mismatchCount: number
  offsets: OffsetComparison[]
}

/**
 * Overall validation result
 */
export interface ComparisonResult {
  totalFrames: number
  framesValidated: number
  totalOffsets: number
  offsetsMatched: number
  offsetsMismatched: number
  tolerance: number
  details: FrameComparison[]
}

/**
 * Comprehensive validation report
 */
export interface ValidationReport {
  success: boolean
  timestamp: string
  section: string // Which section was tested
  framesValidated: number
  offsetsChecked: number
  offsetsMatched: number
  offsetsMismatched: number
  matchPercentage: number
  tolerance: number
  positionStats: {
    count: number
    minX: number
    maxX: number
    minY: number
    maxY: number
    rangeX: number
    rangeY: number
  }
  errors: {
    avgErrorMagnitude: number
    maxErrorMagnitude: number
    maxErrorFrame: number
    maxErrorOffset: string
  }
  warnings: string[]
  comparison: ComparisonResult
  executionTime: number
}

/**
 * Function signatures (to be implemented in next steps)
 */

/**
 * Reconstruct offset_map structure from calculated frame positions
 *
 * @param positions - Calculated frame positions from BFS
 * @param manifest - Manifest containing frame structure
 * @returns ReconstructedOffsetMap matching original offset_map structure
 */
export function reconstructOffsetMapFromPositions(
  positions: Map<number, WorldPosition>,
  manifest: Manifest
): ReconstructedOffsetMap {
  const all_pairs: Record<string, { dx: number; dy: number }> = {}

  // For each frame in manifest
  manifest.frames.forEach(frame => {
    const framePos = positions.get(frame.id)
    if (!framePos) {
      console.warn(`⚠️  Frame ${frame.id} has no calculated position`)
      return
    }

    // For each neighbor offset in this frame
    Object.entries(frame.offsets).forEach(([neighborIdStr]) => {
      const neighborId = parseInt(neighborIdStr, 10)
      const neighborPos = positions.get(neighborId)

      if (!neighborPos) {
        console.warn(`⚠️  Frame ${neighborId} (neighbor of ${frame.id}) has no position`)
        return
      }

      // Key format: "frameA_frameB"
      const key = `${frame.id}_${neighborId}`

      // Reconstruct offset by calculating difference between positions
      // offset = neighbor_position - current_position
      const reconstructedOffset = {
        dx: Math.round(neighborPos.x - framePos.x),
        dy: Math.round(neighborPos.y - framePos.y),
      }

      all_pairs[key] = reconstructedOffset
    })
  })

  return { all_pairs }
}

/**
 * Compare calculated offsets against original offset_map
 *
 * @param calculated - Reconstructed offset_map from positions
 * @param original - Original offset_map.json
 * @param tolerance - Acceptable error in pixels (default ±1)
 * @returns ComparisonResult with detailed frame-by-frame analysis
 */
export function compareOffsetMaps(
  calculated: ReconstructedOffsetMap,
  original: OffsetMap,
  tolerance: number = 1
): ComparisonResult {
  const details: FrameComparison[] = []
  let totalOffsets = 0
  let offsetsMatched = 0
  let offsetsMismatched = 0
  const frameMap = new Map<number, FrameComparison>()

  // Compare each offset pair
  Object.entries(calculated.all_pairs).forEach(([key, calculatedOffset]) => {
    const originalOffset = original.all_pairs[key]

    // Get frame ID from key (format: "frameA_frameB")
    const frameId = parseInt(key.split('_')[0], 10)

    // Initialize frame comparison if not exists
    if (!frameMap.has(frameId)) {
      frameMap.set(frameId, {
        frameId,
        offsetCount: 0,
        matchCount: 0,
        mismatchCount: 0,
        offsets: [],
      })
    }

    const frameComp = frameMap.get(frameId)!
    frameComp.offsetCount++
    totalOffsets++

    // If offset doesn't exist in original, it's a mismatch
    if (!originalOffset) {
      offsetsMismatched++
      frameComp.mismatchCount++
      frameComp.offsets.push({
        key,
        original: { dx: 0, dy: 0 }, // No original value
        calculated: calculatedOffset,
        match: false,
        error: { dx: calculatedOffset.dx, dy: calculatedOffset.dy },
        errorMagnitude: Math.sqrt(
          calculatedOffset.dx ** 2 + calculatedOffset.dy ** 2
        ),
      })
      return
    }

    // Calculate error
    const errorDx = Math.abs(calculatedOffset.dx - originalOffset.dx)
    const errorDy = Math.abs(calculatedOffset.dy - originalOffset.dy)
    const errorMagnitude = Math.sqrt(errorDx ** 2 + errorDy ** 2)

    // Check if within tolerance
    const match = errorDx <= tolerance && errorDy <= tolerance

    if (match) {
      offsetsMatched++
      frameComp.matchCount++
    } else {
      offsetsMismatched++
      frameComp.mismatchCount++
    }

    frameComp.offsets.push({
      key,
      original: { dx: originalOffset.dx, dy: originalOffset.dy },
      calculated: calculatedOffset,
      match,
      error: { dx: errorDx, dy: errorDy },
      errorMagnitude,
    })
  })

  // Collect all frame comparisons
  frameMap.forEach(comp => {
    details.push(comp)
  })

  // Sort by frame ID
  details.sort((a, b) => a.frameId - b.frameId)

  return {
    totalFrames: details.length,
    framesValidated: details.length,
    totalOffsets,
    offsetsMatched,
    offsetsMismatched,
    tolerance,
    details,
  }
}

/**
 * Main validation pipeline
 *
 * Orchestrates all steps:
 * 1. Calculate positions from manifest using BFS
 * 2. Reconstruct offset_map from calculated positions
 * 3. Compare with original offset_map
 * 4. Aggregate results into ValidationReport
 *
 * @param manifest - Loaded manifest.json
 * @param originalOffsetMap - Original offset_map.json for comparison
 * @param sectionName - Name/identifier of section being tested
 * @returns ValidationReport with comprehensive results
 */
export async function validateManifestOffsetCompatibility(
  manifest: Manifest,
  originalOffsetMap: OffsetMap,
  sectionName: string = 'unknown'
): Promise<ValidationReport> {
  const startTime = performance.now()
  const warnings: string[] = []

  try {
    // Step 1: Calculate positions from manifest using sequential calculation
    console.log('📍 Step 1: Calculating frame positions from manifest offsets...')
    const positions = calculateFrameWorldPositionsFromManifest(manifest)

    if (positions.length === 0) {
      throw new Error('Failed to calculate any frame positions')
    }

    // Convert FrameWorldPosition[] to Map<number, WorldPosition>
    const positionsMap = new Map<number, WorldPosition>()
    positions.forEach(pos => {
      positionsMap.set(pos.frameIndex, { x: pos.worldX, y: pos.worldY })
    })

    // Step 2: Reconstruct offset_map from calculated positions
    console.log('🔄 Step 2: Reconstructing offset_map from calculated positions...')
    const reconstructedOffsetMap = reconstructOffsetMapFromPositions(positionsMap, manifest)

    // Step 3: Compare with original offset_map
    console.log('🔍 Step 3: Comparing calculated vs original offsets...')
    const comparison = compareOffsetMaps(reconstructedOffsetMap, originalOffsetMap, 1)

    // Step 4: Calculate statistics
    let totalErrorMagnitude = 0
    let maxErrorMagnitude = 0
    let maxErrorFrame = -1
    let maxErrorOffset = ''

    comparison.details.forEach(frameComp => {
      frameComp.offsets.forEach(offsetComp => {
        totalErrorMagnitude += offsetComp.errorMagnitude
        if (offsetComp.errorMagnitude > maxErrorMagnitude) {
          maxErrorMagnitude = offsetComp.errorMagnitude
          maxErrorFrame = frameComp.frameId
          maxErrorOffset = offsetComp.key
        }
      })
    })

    const avgErrorMagnitude =
      comparison.totalOffsets > 0 ? totalErrorMagnitude / comparison.totalOffsets : 0

    // Get position statistics
    const posStats = {
      count: positionsMap.size,
      minX: 0,
      maxX: 0,
      minY: 0,
      maxY: 0,
      rangeX: 0,
      rangeY: 0,
    }

    if (positionsMap.size > 0) {
      const posArray = Array.from(positionsMap.values())
      const xs = posArray.map(p => p.x)
      const ys = posArray.map(p => p.y)
      posStats.minX = Math.min(...xs)
      posStats.maxX = Math.max(...xs)
      posStats.minY = Math.min(...ys)
      posStats.maxY = Math.max(...ys)
      posStats.rangeX = posStats.maxX - posStats.minX
      posStats.rangeY = posStats.maxY - posStats.minY
    }

    // Step 5: Generate report
    const matchPercentage =
      comparison.totalOffsets > 0
        ? (comparison.offsetsMatched / comparison.totalOffsets) * 100
        : 0

    const success = matchPercentage >= 95 // Consider success if ≥95% match

    if (matchPercentage < 95) {
      warnings.push(`Low match percentage: ${matchPercentage.toFixed(2)}%`)
    }

    if (maxErrorMagnitude > 2) {
      warnings.push(`High max error: ${maxErrorMagnitude.toFixed(2)} pixels at offset ${maxErrorOffset}`)
    }

    const executionTime = performance.now() - startTime

    const report: ValidationReport = {
      success,
      timestamp: new Date().toISOString(),
      section: sectionName,
      framesValidated: comparison.framesValidated,
      offsetsChecked: comparison.totalOffsets,
      offsetsMatched: comparison.offsetsMatched,
      offsetsMismatched: comparison.offsetsMismatched,
      matchPercentage,
      tolerance: comparison.tolerance,
      positionStats: posStats,
      errors: {
        avgErrorMagnitude,
        maxErrorMagnitude,
        maxErrorFrame,
        maxErrorOffset,
      },
      warnings,
      comparison,
      executionTime,
    }

    console.log('✅ Validation complete')
    return report
  } catch (error) {
    const executionTime = performance.now() - startTime
    console.error('❌ Validation failed:', error)

    return {
      success: false,
      timestamp: new Date().toISOString(),
      section: sectionName,
      framesValidated: 0,
      offsetsChecked: 0,
      offsetsMatched: 0,
      offsetsMismatched: 0,
      matchPercentage: 0,
      tolerance: 1,
      positionStats: { count: 0, minX: 0, maxX: 0, minY: 0, maxY: 0, rangeX: 0, rangeY: 0 },
      errors: { avgErrorMagnitude: 0, maxErrorMagnitude: 0, maxErrorFrame: -1, maxErrorOffset: '' },
      warnings: [`Error during validation: ${error instanceof Error ? error.message : String(error)}`],
      comparison: {
        totalFrames: 0,
        framesValidated: 0,
        totalOffsets: 0,
        offsetsMatched: 0,
        offsetsMismatched: 0,
        tolerance: 1,
        details: [],
      },
      executionTime,
    }
  }
}

/**
 * Helper function: Extract offset pairs from manifest
 * For debugging/validation purposes
 *
 * @param manifest - Manifest containing offsets
 * @returns Map of all offset pairs in manifest
 */
export function extractOffsetsFromManifest(
  manifest: Manifest
): Map<string, { dx: number; dy: number }> {
  const offsets = new Map<string, { dx: number; dy: number }>()
  
  // Create frame map for safe lookup by ID (handles non-sequential IDs)
  const frameMap = new Map(manifest.frames.map(f => [f.id, f]))
  let forwardCount = 0
  let reverseCount = 0

  // Extract forward offsets as stored in manifest (to_id > from_id)
  // AND generate reverse offsets for bidirectional navigation
  frameMap.forEach((frame) => {
    Object.entries(frame.offsets).forEach(([neighborIdStr, offset]) => {
      const neighborId = parseInt(neighborIdStr, 10)
      
      // Forward offset (as stored)
      const forwardKey = `${frame.id}_${neighborId}`
      offsets.set(forwardKey, { dx: offset.dx, dy: offset.dy })
      forwardCount++
      
      // Reverse offset (generated by negating dx/dy)
      // Only add if not already stored (avoid duplicates)
      const reverseKey = `${neighborId}_${frame.id}`
      if (!offsets.has(reverseKey)) {
        offsets.set(reverseKey, { 
          dx: -offset.dx,  // Negate for reverse direction
          dy: -offset.dy 
        })
        reverseCount++
      }
    })
  })

  console.log(`[extractOffsetsFromManifest] ✅ Extracted ${forwardCount} forward + ${reverseCount} reverse = ${offsets.size} total offsets`)

  return offsets
}

/**
 * Helper function: Extract offset pairs from original offset_map
 * For comparison purposes
 *
 * @param offsetMap - Original offset_map.json
 * @returns Map of all offset pairs in offset_map (dx/dy only)
 */
export function extractOffsetsFromOffsetMap(
  offsetMap: OffsetMap
): Map<string, { dx: number; dy: number }> {
  const offsets = new Map<string, { dx: number; dy: number }>()

  Object.entries(offsetMap.all_pairs).forEach(([key, pair]) => {
    offsets.set(key, { dx: pair.dx, dy: pair.dy })
  })

  return offsets
}

/**
 * Helper function: Format validation report for console output
 *
 * @param report - ValidationReport to format
 * @returns Formatted string for display
 */
export function formatValidationReport(report: ValidationReport): string {
  const lines: string[] = [
    '\n' + '='.repeat(70),
    'VALIDATION REPORT: Manifest Offset Compatibility',
    '='.repeat(70),
    `Timestamp: ${report.timestamp}`,
    `Section: ${report.section}`,
    `Status: ${report.success ? '✅ PASSED' : '❌ FAILED'}`,
    '',
    '📊 SUMMARY:',
    `  Frames Validated: ${report.framesValidated}`,
    `  Offsets Checked: ${report.offsetsChecked}`,
    `  Offsets Matched: ${report.offsetsMatched}`,
    `  Offsets Mismatched: ${report.offsetsMismatched}`,
    `  Match Percentage: ${report.matchPercentage.toFixed(2)}%`,
    `  Tolerance: ±${report.tolerance} pixel(s)`,
    '',
    '📍 POSITION STATISTICS:',
    `  Frames: ${report.positionStats.count}`,
    `  X Range: ${report.positionStats.minX.toFixed(2)} to ${report.positionStats.maxX.toFixed(2)} (range: ${report.positionStats.rangeX.toFixed(2)})`,
    `  Y Range: ${report.positionStats.minY.toFixed(2)} to ${report.positionStats.maxY.toFixed(2)} (range: ${report.positionStats.rangeY.toFixed(2)})`,
    '',
    '⚠️  ERROR ANALYSIS:',
    `  Avg Error Magnitude: ${report.errors.avgErrorMagnitude.toFixed(2)} pixels`,
    `  Max Error Magnitude: ${report.errors.maxErrorMagnitude.toFixed(2)} pixels`,
    `  Max Error Frame: ${report.errors.maxErrorFrame}`,
    `  Max Error Offset: ${report.errors.maxErrorOffset}`,
  ]

  if (report.warnings.length > 0) {
    lines.push('')
    lines.push('⚠️  WARNINGS:')
    report.warnings.forEach(w => lines.push(`  - ${w}`))
  }

  lines.push('')
  lines.push(`⏱️  Execution Time: ${report.executionTime.toFixed(2)}ms`)
  lines.push('='.repeat(70) + '\n')

  return lines.join('\n')
}
